import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDate, formatDateTime } from "@/lib/helpers";
import { ArrowLeft, Edit, Gauge, ShieldCheck, Wrench } from "lucide-react";
import { toast } from "sonner";

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [noteText, setNoteText] = useState("");

  const load = useCallback(async () => {
    try {
      const [vRes, iRes] = await Promise.all([api.get(`/vehicles/${vehicleId}`), api.get("/instructors")]);
      setVehicle(vRes.data);
      setEditForm(vRes.data);
      setInstructors(iRes.data);
    } catch {
      toast.error("Could not load vehicle");
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    try {
      await api.put(`/vehicles/${vehicleId}`, editForm);
      toast.success("Vehicle updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update vehicle");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/vehicles/${vehicleId}/maintenance-notes`, { note: noteText });
      setNoteText("");
      load();
    } catch {
      toast.error("Could not add note");
    }
  };

  if (loading || !vehicle) return <Skeleton className="h-96 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="vehicle-detail-back-button">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold">{vehicle.make} {vehicle.model} {vehicle.year ? `(${vehicle.year})` : ""}</h1>
          <p className="text-sm text-muted-foreground font-mono-ui">{vehicle.license_plate} {vehicle.vin && `· VIN: ${vehicle.vin}`}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={vehicle.status} />
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-vehicle-button">
            <Edit className="h-4 w-4 mr-2" /> Edit
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> Current Mileage</p>
          <p className="font-semibold text-lg">{vehicle.current_mileage?.toLocaleString() || 0} mi</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Oil Change Due</p>
          <p className="font-semibold text-lg">{vehicle.oil_change_due_mileage ? `${vehicle.oil_change_due_mileage.toLocaleString()} mi` : "-"}</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Tire Rotation Due</p>
          <p className="font-semibold text-lg">{vehicle.tire_rotation_due_mileage ? `${vehicle.tire_rotation_due_mileage.toLocaleString()} mi` : "-"}</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Insurance Exp.</p>
          <p className="font-semibold text-lg">{vehicle.insurance_expiration ? formatDate(vehicle.insurance_expiration) : "-"}</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Registration Exp.</p>
          <p className="font-semibold text-lg">{vehicle.registration_expiration ? formatDate(vehicle.registration_expiration) : "-"}</p>
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4">
          <p className="text-xs text-muted-foreground">Assigned Instructor</p>
          <p className="font-semibold text-lg">{vehicle.assigned_instructor_name || "Unassigned"}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
          <h2 className="font-semibold font-heading flex items-center gap-2"><Wrench className="h-4 w-4" /> Reported Issues</h2>
          {(!vehicle.issues || vehicle.issues.length === 0) ? (
            <p className="text-sm text-muted-foreground">No issues reported — this vehicle has a clean history.</p>
          ) : (
            <div className="divide-y">
              {vehicle.issues.map((i) => (
                <div key={i.id} className="py-2" data-testid="vehicle-issue-history-row">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">{i.description}</p>
                    <StatusBadge status={i.status} />
                  </div>
                  <p className="text-xs text-muted-foreground">{i.reported_by_name} · {formatDateTime(i.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3">
          <h2 className="font-semibold font-heading">Maintenance Notes</h2>
          <div className="space-y-2">
            <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a maintenance note..." data-testid="vehicle-maintenance-note-input" />
            <Button size="sm" onClick={addNote} data-testid="vehicle-maintenance-note-submit-button">Add Note</Button>
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {(vehicle.maintenance_notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="text-sm border-l-2 border-[hsl(var(--role-accent))] pl-3">
                <p>{n.note}</p>
                <p className="text-xs text-muted-foreground">{n.created_by} · {formatDateTime(n.created_at)}</p>
              </div>
            ))}
            {(!vehicle.maintenance_notes || vehicle.maintenance_notes.length === 0) && (
              <p className="text-sm text-muted-foreground">No maintenance notes yet.</p>
            )}
          </div>
        </section>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="edit-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Edit Vehicle</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>Current mileage</Label>
              <Input type="number" value={editForm.current_mileage || 0} onChange={(e) => setEditForm((f) => ({ ...f, current_mileage: parseFloat(e.target.value) }))} data-testid="edit-vehicle-mileage-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={editForm.status || "available"} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))} data-testid="edit-vehicle-status-select">
                <option value="available">Available</option>
                <option value="in_use">In Use</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Oil change due (mileage)</Label>
              <Input type="number" value={editForm.oil_change_due_mileage || ""} onChange={(e) => setEditForm((f) => ({ ...f, oil_change_due_mileage: parseFloat(e.target.value) }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Tire rotation due (mileage)</Label>
              <Input type="number" value={editForm.tire_rotation_due_mileage || ""} onChange={(e) => setEditForm((f) => ({ ...f, tire_rotation_due_mileage: parseFloat(e.target.value) }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Insurance expiration</Label>
              <Input type="date" value={editForm.insurance_expiration || ""} onChange={(e) => setEditForm((f) => ({ ...f, insurance_expiration: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Registration expiration</Label>
              <Input type="date" value={editForm.registration_expiration || ""} onChange={(e) => setEditForm((f) => ({ ...f, registration_expiration: e.target.value }))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Assigned instructor</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={editForm.assigned_instructor_id || ""} onChange={(e) => setEditForm((f) => ({ ...f, assigned_instructor_id: e.target.value }))}>
                <option value="">Unassigned</option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>{i.full_name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-vehicle-save-button">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
